import {
  DesktopPanelConfig,
  DesktopPopupConfig,
  DesktopStepConfig,
  DesktopStepType,
  MobileDrawerConfig,
  MobileModalConfig,
  MobileStepConfig,
  MobileStepType,
  SpotlightSize,
} from "./spotlight-schema";

/** The S/M/L presets, i.e. every size that maps to a built-in pixel value. */
export type PresetSpotlightSize = Exclude<SpotlightSize, SpotlightSize.CUSTOM>;

/** Stands in for a custom size whose pixel value hasn't been authored yet. */
const FALLBACK_PRESET: PresetSpotlightSize = SpotlightSize.M;

function presetSize(size: SpotlightSize): PresetSpotlightSize {
  return size === SpotlightSize.CUSTOM ? FALLBACK_PRESET : size;
}

export const POPUP_DIMENSIONS: Record<
  PresetSpotlightSize,
  { width: number; height: number }
> = {
  [SpotlightSize.S]: { width: 320, height: 240 },
  [SpotlightSize.M]: { width: 560, height: 240 },
  [SpotlightSize.L]: { width: 720, height: 540 },
};

export const PANEL_WIDTHS: Record<PresetSpotlightSize, number> = {
  [SpotlightSize.S]: 360,
  [SpotlightSize.M]: 480,
  [SpotlightSize.L]: 640,
};

/**
 * Drawer heights in px. These were percentages of the phone frame (0.3 / 0.5 /
 * 0.75), which is why M cut the screen in half; these are the equivalents on
 * the 812px frame, rounded. Sizes are pixels everywhere now.
 */
export const DRAWER_HEIGHT: Record<PresetSpotlightSize, number> = {
  [SpotlightSize.S]: 240,
  [SpotlightSize.M]: 400,
  [SpotlightSize.L]: 600,
};

export const MOBILE_WIDTH = 375;
export const MOBILE_HEIGHT = 812;

export interface SpotlightContainerStyles {
  width?: number | string;
  height?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  overflow?: string;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  backgroundColor?: string;
}

/**
 * The resolved size of a step, in pixels, on whichever axes its placement
 * actually constrains. A panel spans the viewport height and a drawer spans the
 * viewport width, so each reports only the axis it owns.
 */
export interface StepDimensions {
  widthPx?: number;
  heightPx?: number;
  /** True when the height is a minimum the step may grow past. */
  heightIsFloor: boolean;
}

/**
 * Panel width, honoring the Advanced settings override that predates the
 * Custom size. A stored `width > 0` is what production has always rendered, so
 * it still wins and the step reads back as Custom.
 */
export function resolvePanelWidthPx(config: DesktopPanelConfig): number {
  const override = config.advancedSettings.width;
  if (override > 0) {
    return override;
  }
  if (config.size === SpotlightSize.CUSTOM) {
    return config.customWidthPx ?? PANEL_WIDTHS[FALLBACK_PRESET];
  }
  return PANEL_WIDTHS[config.size];
}

/** Whether the inspector should show this step as Custom rather than S/M/L. */
export function isCustomStepSize(
  config: DesktopStepConfig | MobileStepConfig,
): boolean {
  if (!("size" in config)) {
    return false;
  }
  if (config.size === SpotlightSize.CUSTOM) {
    return true;
  }
  return (
    config.type === DesktopStepType.PANEL && config.advancedSettings.width > 0
  );
}

/**
 * The single source of step size math. Every surface that draws a step — the
 * storefront modal, the builder canvas, the review page, the template gallery —
 * resolves through here so they cannot drift apart.
 */
export function resolveStepDimensions(
  config: DesktopStepConfig | MobileStepConfig,
): StepDimensions {
  const heightIsFloor = config.growHeightWithContent;

  switch (config.type) {
    case DesktopStepType.POPUP:
    case MobileStepType.MODAL: {
      const preset = POPUP_DIMENSIONS[presetSize(config.size)];
      const isCustom = config.size === SpotlightSize.CUSTOM;
      return {
        widthPx: (isCustom ? config.customWidthPx : undefined) ?? preset.width,
        heightPx:
          (isCustom ? config.customHeightPx : undefined) ?? preset.height,
        heightIsFloor,
      };
    }
    case DesktopStepType.PANEL:
      // Height is the viewport; only width is authorable.
      return { widthPx: resolvePanelWidthPx(config), heightIsFloor };
    case MobileStepType.DRAWER:
      // Width is the viewport; only height is authorable.
      return { heightPx: resolveDrawerHeightPx(config), heightIsFloor };
    // Full screen fills the viewport; there is no size to resolve. Both enum
    // members are needed even though they share a string value — TypeScript
    // checks exhaustiveness against the members, not their values.
    case DesktopStepType.FULLSCREEN:
    case MobileStepType.FULLSCREEN:
      return { heightIsFloor };
  }
}

export function resolveDrawerHeightPx(config: MobileDrawerConfig): number {
  if (config.size === SpotlightSize.CUSTOM) {
    return config.customHeightPx ?? DRAWER_HEIGHT[FALLBACK_PRESET];
  }
  return DRAWER_HEIGHT[config.size];
}

function getPopupContainerStyles(
  config: DesktopPopupConfig,
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): SpotlightContainerStyles {
  const settings = config.advancedSettings;

  return {
    ...boxSizeStyles(resolveStepDimensions(config)),
    maxHeight: viewportMaxHeight(viewportHeight, dynamicViewportUnitsSupported),
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    // Background color is handled by the Flex in SpotlightFormContent.
    // Setting it here would double-apply it, which does weird things when the color
    // isn't fully opaque.
  };
}

function getModalContainerStyles(
  config: MobileModalConfig,
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): SpotlightContainerStyles {
  const settings = config.advancedSettings;

  return {
    ...boxSizeStyles(resolveStepDimensions(config)),
    maxHeight: viewportMaxHeight(viewportHeight, dynamicViewportUnitsSupported),
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
  };
}

/**
 * Width is a hard cap; height is a floor the step may grow past, or a hard cap
 * that clips. `overflow` has to move in lockstep with which one it is — a
 * growing box that clips would defeat the point. A growing box scrolls once the
 * viewport cap stops it.
 */
function boxSizeStyles({
  widthPx,
  heightPx,
  heightIsFloor,
}: StepDimensions): SpotlightContainerStyles {
  if (heightIsFloor) {
    return { width: widthPx, minHeight: heightPx, overflow: "auto" };
  }
  return { width: widthPx, height: heightPx, overflow: "hidden" };
}

function viewportMaxHeight(
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): number | string {
  return dynamicViewportUnitsSupported ? "100dvh" : viewportHeight;
}

function getPanelContainerStyles(
  config: DesktopPanelConfig,
  viewportHeight: number,
): SpotlightContainerStyles {
  const settings = config.advancedSettings;

  return {
    width: resolvePanelWidthPx(config),
    height: viewportHeight ?? "100%",
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

function getFullscreenContainerStyles(
  config: DesktopStepConfig | MobileStepConfig,
): SpotlightContainerStyles {
  const settings = config.advancedSettings;

  return {
    width: "100%",
    height: "100%",
    backgroundColor: settings.background,
  };
}

function getDrawerContainerStyles(
  config: MobileDrawerConfig,
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): SpotlightContainerStyles {
  const height = resolveDrawerHeightPx(config);
  const settings = config.advancedSettings;
  // The old percentage sizing implicitly kept the drawer inside the viewport.
  // Pixels don't, so cap it — an L drawer is taller than a short phone.
  const maxHeight = viewportMaxHeight(
    viewportHeight,
    dynamicViewportUnitsSupported,
  );

  return {
    width: "100%",
    ...(config.growHeightWithContent
      ? { minHeight: height, maxHeight, overflow: "auto" }
      : { height, maxHeight, overflow: "hidden" }),
    borderRadius: settings.cornerRadius,
    borderWidth: settings.border.weight,
    borderColor: settings.border.color,
    backgroundColor: settings.background,
  };
}

function getDesktopContainerStyles(
  config: DesktopStepConfig,
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): SpotlightContainerStyles {
  switch (config.type) {
    case DesktopStepType.POPUP:
      return getPopupContainerStyles(
        config,
        viewportHeight,
        dynamicViewportUnitsSupported,
      );
    case DesktopStepType.PANEL:
      return getPanelContainerStyles(config, viewportHeight);
    case DesktopStepType.FULLSCREEN:
      return getFullscreenContainerStyles(config);
  }
}

function getMobileContainerStyles(
  config: MobileStepConfig,
  viewportHeight: number,
  dynamicViewportUnitsSupported: boolean,
): SpotlightContainerStyles {
  switch (config.type) {
    case MobileStepType.DRAWER:
      return getDrawerContainerStyles(
        config,
        viewportHeight,
        dynamicViewportUnitsSupported,
      );
    case MobileStepType.MODAL:
      return getModalContainerStyles(
        config,
        viewportHeight,
        dynamicViewportUnitsSupported,
      );
    case MobileStepType.FULLSCREEN:
      return getFullscreenContainerStyles(config);
  }
}

export function getContainerStyles(
  config: DesktopStepConfig | MobileStepConfig,
  isMobile: boolean,
  viewportHeight: number,
  dynamicViewportUnitsSupported = true,
): SpotlightContainerStyles {
  if (isMobile) {
    return getMobileContainerStyles(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      config as MobileStepConfig,
      viewportHeight,
      dynamicViewportUnitsSupported,
    );
  }
  return getDesktopContainerStyles(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    config as DesktopStepConfig,
    viewportHeight,
    dynamicViewportUnitsSupported,
  );
}
